/* eslint-disable max-lines -- Pre-existing violation */
import {closeCurrentDialog, openDialog} from 'platform/components';
import {ThemeColorPath} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {CodeResponseBody} from '@dms/api/core';
import {InterestResponseBodyV2Read} from '@dms/api/finance';
import {
  BusinessCaseActivityResponseBody,
  InterestActivityResponseBody,
  SalesActivityTypeEnum,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {DirectSaleActivityResponseBody} from '@dms/api/warehouse';
import {ServiceCaseActivityKind, ServiceCaseActivityResponseBody} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {businessCaseRoutes, interestRoutes, telephonyRoutes} from '@dms/routes';
import {EMPTY_PLACEHOLDER, SentMessagePreviewDialog} from '@dms/shared';

import {buildArray, composePath, isNotNilOrEmpty, valueOrPlaceholder} from 'shared/web';

import {MessageDialog} from '../components/ActivityTab/components/MessageDialog';
import {LogContactDialog} from '../components/LogContactDialog/LogContactDialog';
import {IconColors} from '../constants/activityItem';
import {AUTOMATIC_CUSTOMER_MESSAGE_NAME_KEYS} from '../constants/AUTOMATIC_CUSTOMER_MESSAGE_NAME_KEYS';
import {TransformedActivityItem, ActivityItemDataLink} from '../types/ActivitySidebarAction';
import {getTaskIcon} from './getTaskIcon';

type ActivityItem = (
  | InterestActivityResponseBody
  | BusinessCaseActivityResponseBody
  | ServiceCaseActivityResponseBody
  | DirectSaleActivityResponseBody
) & {
  color?: ThemeColorPath;
};

const handleOpenLogContactDialog = (
  item: InterestActivityResponseBody | BusinessCaseActivityResponseBody
) => {
  openDialog(<LogContactDialog salesActivity={item} />, {
    id: 'LOG_CONTACT_DIALOG',
    title: i18n.t('entity.activityPanel.logContact.editLog'),
    scrollBehavior: 'outside',
  });
};

const handleOpenMessageDialog = (body: string, title: string, id: string) => {
  openDialog(<MessageDialog message={body} />, {
    id,
    title,
    buttons: [
      {
        variant: 'secondary',
        title: i18n.t('general.actions.close'),
        onClick: closeCurrentDialog,
      },
    ],
  });
};

const handleOpenEmailPreviewDialog = (item: ActivityItem, title: string, id: string) => {
  const attachments = item.relatedMessage?.attachments?.map((attachment) => ({
    id: attachment.uri,
    title: attachment.filename,
    remoteUrl: attachment.uri,
    contentType: attachment.contentType,
  }));

  const sender = item.relatedMessage?.sender.name ?? EMPTY_PLACEHOLDER;

  openDialog(
    <SentMessagePreviewDialog
      attachments={attachments}
      body={valueOrPlaceholder(item.relatedMessage?.body)}
      recipient={valueOrPlaceholder(item.relatedMessage?.sentTo)}
      sender={sender}
      sentAt={item.timestamp}
      subject={valueOrPlaceholder(item.relatedMessage?.subject)}
    />,
    {
      id,
      title,
      size: 'xLarge',
      buttons: [
        {
          variant: 'secondary',
          title: i18n.t('general.actions.close'),
          onClick: closeCurrentDialog,
        },
      ],
    }
  );
};

/** "Detail" opens the stored call's page; nothing when the viewer may not open calls. */
const getCallDetailLinks = (item: ActivityItem, canOpenCall?: boolean) => {
  const callId = 'relatedCall' in item ? (item.relatedCall?.id ?? null) : null;

  return buildArray<ActivityItemDataLink>().whenNotNil(
    canOpenCall === true ? callId : null,
    (id) => ({
      label: i18n.t('general.labels.detail'),
      url: composePath(telephonyRoutes.callDetail, {params: {id}}),
    })
  );
};

const getRelatedTestDriveScheduledAt = (item: ActivityItem): string | null =>
  'relatedTestDrive' in item ? (item.relatedTestDrive?.scheduledAt ?? null) : null;

const TITLE_SEPARATOR = ' · ';

const getCustomerRatingTitle = (item: ActivityItem) => {
  const rating = 'customerRating' in item ? item.customerRating : null;
  if (isNil(rating)) {
    return i18n.t('entity.activityPanel.activity.customerRatedPurchaseUnknown');
  }
  return [
    i18n.t('entity.activityPanel.activity.customerRatedPurchase', {rating: rating.rating}),
    isNotNil(rating.recommendation)
      ? i18n.t('entity.activityPanel.activity.customerRatedPurchaseRecommendation', {
          recommendation: rating.recommendation,
        })
      : null,
    rating.hasComment
      ? i18n.t('entity.activityPanel.activity.customerRatedPurchaseWithComment')
      : null,
    i18n.t('entity.activityPanel.activity.customerRatedPurchaseViaOnlineOffer'),
  ]
    .filter(isNotNil)
    .join(TITLE_SEPARATOR);
};

const getSentAfterSaleItem = (item: ActivityItem, title: string): TransformedActivityItem => ({
  id: item.id,
  title,
  timestamp: item.timestamp,
  color: IconColors.GREEN,
  userName: item.relatedMessage?.sentTo ?? undefined,
  interestType: item.relatedMessage?.subject ?? undefined,
  icon: isNotNil(item.relatedMessage?.subject) ? 'communication/email' : 'communication/textsms',
  links: buildArray<ActivityItemDataLink>().when(isNotNil(item.relatedMessage), {
    label: i18n.t('general.labels.detail'),
    onClick: () =>
      isNotNil(item.relatedMessage?.subject)
        ? handleOpenEmailPreviewDialog(
            item,
            i18n.t('entity.activityPanel.activity.actions.emailSent'),
            'MESSAGE_DIALOG_EMAIL'
          )
        : handleOpenMessageDialog(
            item.relatedMessage?.body ?? '',
            i18n.t('entity.activityPanel.activity.actions.smsSent'),
            'MESSAGE_DIALOG_SMS'
          ),
  }),
});

const SMS_RESOURCE_ID = 'SMS';

const getAutomaticCustomerMessageName = (item: ActivityItem) => {
  const id = 'customerMessageId' in item ? item.customerMessageId : null;
  const key =
    !isNil(id) && Object.hasOwn(AUTOMATIC_CUSTOMER_MESSAGE_NAME_KEYS, id)
      ? AUTOMATIC_CUSTOMER_MESSAGE_NAME_KEYS[id]
      : undefined;
  return i18n.t(key ?? 'entity.automaticCustomerMessage.name.unknown');
};

// eslint-disable-next-line max-lines-per-function, max-params -- Pre-existing violation
export const transformActivityItem = (
  item: ActivityItem,
  interest?: InterestResponseBodyV2Read,
  overrides?: {
    source?: CodeResponseBody;
    closeReason?: CodeResponseBody;
    userName?: string;
    message?: string;
    serviceOrderNumber?: string;
    vehicleTitle?: string;
    manualActivity?: {isEditable: boolean; onClick: () => void};
    /** The viewer may open a stored call's page; without it call rows carry no link. */
    canOpenCall?: boolean;
  },
  parentResourceId?: EntityResourceIds,
  openEditTaskFormDialog?: (id: string) => void,
  handleToggleTab?: (tabId: string) => void
): TransformedActivityItem => {
  const isServiceCaseActivity = parentResourceId === EntityResourceIds.serviceCase;

  const manualActivity =
    overrides?.manualActivity ??
    ('serviceOrderId' in item || !('reasonCloseCodeId' in item)
      ? {isEditable: false}
      : {isEditable: item.isEditable, onClick: () => handleOpenLogContactDialog(item)});
  const reasonCloseNote = 'reasonCloseNote' in item ? item.reasonCloseNote : null;

  return match<SalesActivityTypeEnum | ServiceCaseActivityKind, TransformedActivityItem>(
    item.activityType
  )
    .with('SERVICE_CASE_CREATED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.serviceCaseCreated'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      icon: 'action/build',
    }))
    .with('SERVICE_CASE_PLANNED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.serviceCasePlanned'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      icon: 'action/calendar_today',
    }))
    .with('SERVICE_CASE_CHECK_IN_COMPLETED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.serviceCaseCheckInCompleted'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      icon: 'navigation/arrow_upward',
    }))
    .with('SERVICE_CASE_HANDOVER_COMPLETED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.serviceCaseHandoverCompleted'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      icon: 'navigation/arrow_downward',
    }))
    .with('BUSINESS_CASE_LINKED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.relatedBusinessCase'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      interestType: 'businessCaseCode' in item ? item.businessCaseCode : null,
      icon: 'places/business_center',
      links: buildArray<ActivityItemDataLink>().when(
        'businessCaseId' in item && isNotNilOrEmpty(item.businessCaseId),
        () => ({
          label: i18n.t('entity.activityPanel.activity.actions.openBusinessCase'),
          url: composePath(businessCaseRoutes.overview, {
            params: {id: 'businessCaseId' in item ? (item.businessCaseId ?? '') : ''},
          }),
        })
      ),
    }))
    .with('INTEREST_CREATED', () => ({
      id: item.id,
      color: IconColors.BLUE,
      title: i18n.t('entity.activityPanel.activity.interestCreated'),
      interestType: overrides?.source?.name,
      description: item.description,
      userName: item.userName,
      userId: item.userId,
      timestamp: item.timestamp,
      icon: 'content/next_week',
      links: buildArray<ActivityItemDataLink>().when(
        parentResourceId === EntityResourceIds.businessCase,
        {
          label: i18n.t('entity.activityPanel.activity.actions.openInterest'),
          url: composePath(interestRoutes.detailOverview, {
            params: {id: 'interestId' in item ? (item.interestId ?? '') : ''},
          }),
        }
      ),
    }))
    .with('INTEREST_OPENED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.interestOpened'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      icon: 'content/next_week',
      links: buildArray<ActivityItemDataLink>().when(
        parentResourceId === EntityResourceIds.businessCase,
        {
          label: i18n.t('entity.activityPanel.activity.actions.openInterest'),
          url: composePath(interestRoutes.detailOverview, {
            params: {id: 'interestId' in item ? (item.interestId ?? '') : ''},
          }),
        }
      ),
    }))
    .with('INTEREST_OPENED_ON_CUSTOMER_CONTACT', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.interestOpenedOnCustomerContact'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      icon: 'content/next_week',
      links: buildArray<ActivityItemDataLink>().when(
        parentResourceId === EntityResourceIds.businessCase,
        {
          label: i18n.t('entity.activityPanel.activity.actions.openInterest'),
          url: composePath(interestRoutes.detailOverview, {
            params: {id: 'interestId' in item ? (item.interestId ?? '') : ''},
          }),
        }
      ),
    }))
    .with('INTEREST_REOPENED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.interestReopened'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      icon: 'content/next_week',
      links: buildArray<ActivityItemDataLink>().when(
        parentResourceId === EntityResourceIds.businessCase,
        () => ({
          label: i18n.t('entity.activityPanel.activity.actions.openInterest'),
          url: composePath(interestRoutes.detailOverview, {
            params: {id: 'interestId' in item ? (item.interestId ?? '') : ''},
          }),
        })
      ),
    }))
    .with('INTEREST_CLOSED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.interestClosed'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      userName: item.userName,
      userId: item.userId,
      description: interest?.reasonCloseNote ?? overrides?.closeReason?.name ?? item.description,
      icon: 'content/next_week',
      links: buildArray<ActivityItemDataLink>().when(
        parentResourceId === EntityResourceIds.businessCase,
        {
          label: i18n.t('entity.activityPanel.activity.actions.openInterest'),
          url: composePath(interestRoutes.detailOverview, {
            params: {id: 'interestId' in item ? (item.interestId ?? '') : ''},
          }),
        }
      ),
    }))
    .with('INTEREST_OPPORTUNITY_LOST', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.interestOpportunityLost'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      userName: item.userName,
      userId: item.userId,
      description: reasonCloseNote ?? overrides?.closeReason?.name ?? item.description,
      icon: 'custom/business_case_sad',
      links: buildArray<ActivityItemDataLink>().when(reasonCloseNote, {
        label: i18n.t('general.labels.note'),
        tooltip: [reasonCloseNote ?? '', overrides?.closeReason?.name ?? ''],
      }),
    }))
    .with('BUSINESS_CASE_CREATED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.businessCaseCreated'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      description: item.description,
      icon: 'places/business_center',
      links: buildArray<ActivityItemDataLink>().when(
        parentResourceId === EntityResourceIds.interest,
        {
          label: i18n.t('entity.activityPanel.activity.actions.openBusinessCase'),
          url: composePath(businessCaseRoutes.overview, {
            params: {id: 'businessCaseId' in item ? (item.businessCaseId ?? '') : ''},
          }),
        }
      ),
    }))
    .with('BUSINESS_CASE_CLOSED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.businessCaseClosed'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      userName: item.userName,
      userId: item.userId,
      description: item.description,
      icon: 'places/business_center',
      links: buildArray<ActivityItemDataLink>().when(
        parentResourceId === EntityResourceIds.interest,
        {
          label: i18n.t('entity.activityPanel.activity.actions.openBusinessCase'),
          url: composePath(businessCaseRoutes.overview, {
            params: {id: 'businessCaseId' in item ? (item.businessCaseId ?? '') : ''},
          }),
        }
      ),
    }))
    .with('BUSINESS_CASE_OPPORTUNITY_LOST', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.businessCaseOpportunityLost'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      userName: item.userName,
      userId: item.userId,
      description: reasonCloseNote ?? item.description,
      icon: 'custom/business_case_sad',
      links: buildArray<ActivityItemDataLink>().when(reasonCloseNote, {
        label: i18n.t('general.labels.note'),
        tooltip: [reasonCloseNote ?? '', overrides?.closeReason?.name ?? ''],
      }),
    }))
    .with('MESSAGE_TO_CUSTOMER', () => ({
      id: item.id,
      title: item.subject ?? i18n.t('entity.activityPanel.activity.messageToCustomer'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      interestType: i18n.t('entity.activityPanel.activity.messageToCustomer'),
      userId: item.userId,
      userName: item.userName,
      description: item.description,
      icon: 'communication/comment',
      ...manualActivity,
    }))
    .with('CALL_FROM_CUSTOMER', () => ({
      id: item.id,
      title: item.subject ?? i18n.t('entity.activityPanel.activity.callFromCustomer'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      interestType: i18n.t('entity.activityPanel.activity.callFromCustomer'),
      userName: item.userName,
      userId: item.userId,
      description: item.description,
      icon: 'notification/phone_inbound',
      ...manualActivity,
    }))
    .with('CALL_TO_CUSTOMER', () => ({
      id: item.id,
      title: item.subject ?? i18n.t('entity.activityPanel.activity.callToCustomer'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      interestType: i18n.t('entity.activityPanel.activity.callToCustomer'),
      userName: item.userName,
      userId: item.userId,
      description: item.description,
      icon: 'notification/phone_outbound',
      ...manualActivity,
    }))
    .with('MESSAGE_FROM_CUSTOMER', () => ({
      id: item.id,
      title: item.subject ?? i18n.t('entity.activityPanel.activity.messageFromCustomer'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      interestType: i18n.t('entity.activityPanel.activity.messageFromCustomer'),
      userName: item.userName,
      userId: item.userId,
      description: item.description,
      icon: 'communication/comment',
      ...manualActivity,
    }))
    .with('CUSTOMER_VISIT', () => ({
      id: item.id,
      title: item.subject ?? i18n.t('entity.activityPanel.activity.customerVisit'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      interestType: i18n.t('entity.activityPanel.activity.customerVisit'),
      description: item.description,
      icon: 'communication/location_on',
      ...manualActivity,
    }))
    .with('CONTRACT_CLOSED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.contractClosed'),
      icon: 'action/contract_closed',
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
    }))
    .with('AUCTION_PUBLISHED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.auctionPublished'),
      icon: 'action/build',
      timestamp: item.timestamp,
      color: IconColors.BLUE,
      userName: item.userName,
      userId: item.userId,
    }))
    .with('AUCTION_OFFER_RECEIVED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.auctionOfferReceived'),
      icon: 'communication/comment',
      timestamp: item.timestamp,
      color: IconColors.BLUE,
      userName: item.userName,
      userId: item.userId,
    }))
    .with('AUCTION_BIDDING_CLOSED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.auctionBiddingClosed'),
      icon: 'action/contract_closed',
      timestamp: item.timestamp,
      color: IconColors.BLUE,
      userName: item.userName,
      userId: item.userId,
    }))
    .with('AUCTION_WINNER_CONFIRMED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.auctionWinnerConfirmed'),
      icon: 'action/build',
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
    }))
    .with('AUCTION_TRANSACTION_STATUS_CHANGED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.auctionTransactionStatusChanged'),
      icon: 'action/contract_closed',
      timestamp: item.timestamp,
      color: IconColors.BLUE,
      userName: item.userName,
      userId: item.userId,
    }))
    .with('DEPOSIT_PAYMENT_ISSUED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.depositPaymentIssued'),
      icon: 'action/deposit_payment',
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      links: buildArray<ActivityItemDataLink>().when(
        parentResourceId === EntityResourceIds.interest,
        {
          label: i18n.t('general.labels.detail'),
          url: composePath(businessCaseRoutes.checkout, {
            params: {id: 'businessCaseId' in item ? (item.businessCaseId ?? '') : ''},
            queryParams: {'SALE-CheckoutOrder': 'payment'},
          }),
        }
      ),
    }))
    .with('BALANCE_PAYMENT_ISSUED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.balancePaymentIssued'),
      icon: 'action/invoice_issued',
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      links: buildArray<ActivityItemDataLink>().when(
        parentResourceId === EntityResourceIds.interest,
        {
          label: i18n.t('general.labels.detail'),
          url: composePath(businessCaseRoutes.checkout, {
            params: {id: 'businessCaseId' in item ? (item.businessCaseId ?? '') : ''},
            queryParams: {'SALE-CheckoutOrder': 'payment'},
          }),
        }
      ),
    }))
    .with('PURCHASE_PAYMENT_ISSUED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.purchasePaymentIssued'),
      icon: 'action/invoice_issued',
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      description: item.description,
      userName: item.userName,
      userId: item.userId,
      links: buildArray<ActivityItemDataLink>().when(
        parentResourceId === EntityResourceIds.interest,
        {
          label: i18n.t('general.labels.detail'),
          url: composePath(businessCaseRoutes.checkout, {
            params: {id: 'businessCaseId' in item ? (item.businessCaseId ?? '') : ''},
            queryParams: {'SALE-CheckoutOrder': 'payment'},
          }),
        }
      ),
    }))
    .with('PAYMENT_PAID', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.paymentPaid'),
      icon: 'action/payment_accepted',
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      userName: item.userName,
      userId: item.userId,
      links: buildArray<ActivityItemDataLink>().when(
        parentResourceId === EntityResourceIds.interest,
        {
          label: i18n.t('general.labels.detail'),
          url: composePath(businessCaseRoutes.checkout, {
            params: {id: 'businessCaseId' in item ? (item.businessCaseId ?? '') : ''},
            queryParams: {'SALE-CheckoutOrder': 'payment'},
          }),
        }
      ),
    }))
    .with('TASK_CREATED', () => ({
      id: item.id,
      title: item.relatedTask?.title ?? item.description ?? '',
      timestamp: item.timestamp,
      icon: getTaskIcon(item.relatedTask?.type),
      color: item.color ?? IconColors.GREEN,
      userId: item.userId,
      userName: isNotNilOrEmpty(overrides?.userName)
        ? overrides?.userName
        : (item.userName ?? undefined),
      description: item.relatedTask?.description,
      links: buildArray<ActivityItemDataLink>().when(item.relatedTask, {
        label: i18n.t('general.labels.detail'),
        onClick: () => openEditTaskFormDialog && openEditTaskFormDialog(item.relatedTask!.id),
      }),
    }))
    .with('TASK_FINISHED', () => ({
      id: item.id,
      title: item.relatedTask?.title ?? item.description ?? '',
      timestamp: item.timestamp,
      icon: getTaskIcon(item.relatedTask?.type),
      color: IconColors.GREEN,
      userId: item.userId,
      userName: isNotNilOrEmpty(overrides?.userName)
        ? overrides?.userName
        : (item.userName ?? undefined),
      description: item.relatedTask?.description ?? undefined,
      links: buildArray<ActivityItemDataLink>().when(item.relatedTask, {
        label: i18n.t('general.labels.detail'),
        onClick: () => openEditTaskFormDialog && openEditTaskFormDialog(item.relatedTask!.id),
      }),
    }))
    .with('ONLINE_OFFER_SHARING_CANCELED', 'BUYING_ONLINE_OFFER_SHARING_CANCELED', () => ({
      id: item.id,
      timestamp: item.timestamp,
      color: IconColors.RED,
      userName: item.userName,
      userId: item.userId,
      title: i18n.t('entity.activityPanel.activity.onlineOfferSharingCanceled'),
      icon: 'content/mail_opened',
    }))
    .with('ONLINE_OFFER_SENT', 'BUYING_ONLINE_OFFER_SENT', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.onlineOfferSent'),
      timestamp: item.timestamp,
      userName: item.userName,
      userId: item.userId,
      color: IconColors.GREEN,
      icon: 'content/drafts',
      links: buildArray<ActivityItemDataLink>().add({
        label: i18n.t('general.labels.detail'),
        onClick: () =>
          handleOpenEmailPreviewDialog(
            item,
            i18n.t('entity.activityPanel.activity.actions.emailSent'),
            'MESSAGE_DIALOG_ONLINE_OFFER'
          ),
      }),
    }))
    .with('ONLINE_OFFER_EMAIL_OPENED', 'BUYING_ONLINE_OFFER_EMAIL_OPENED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.onlineOfferEmailOpened'),
      icon: 'content/drafts',
      timestamp: item.timestamp,
      userId: item.userId,
      userName: item.userName,
      color: IconColors.BLUE,
      description: i18n.t('entity.activityPanel.activity.onlineOfferEmailOpenedDescription'),
    }))
    .with('INBOUND_CALL', 'TELEPHONY_INBOUND_CALL', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.inboundCall'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      icon: 'communication/call',
      userId: item.userId,
      userName: item.userName,
      description: item.description,
      links: getCallDetailLinks(item, overrides?.canOpenCall),
    }))
    .with('OUTBOUND_CALL', 'TELEPHONY_OUTBOUND_CALL', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.outboundCall'),
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      icon: 'communication/call',
      userId: item.userId,
      userName: item.userName,
      description: item.description,
      links: getCallDetailLinks(item, overrides?.canOpenCall),
    }))
    .with('EMAIL_TO_CUSTOMER', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.emailToCustomer'),
      timestamp: item.timestamp,
      userName: isServiceCaseActivity
        ? (overrides?.userName ?? item.userName)
        : (item.relatedMessage?.sentTo ?? undefined),
      interestType: isServiceCaseActivity
        ? overrides?.serviceOrderNumber
        : (item.relatedMessage?.subject ?? undefined),
      description: isServiceCaseActivity
        ? (overrides?.message ?? item.relatedMessage?.sentTo ?? undefined)
        : undefined,
      color: IconColors.GREEN,
      icon: 'communication/email',
      links: buildArray<ActivityItemDataLink>().add({
        label: i18n.t('general.labels.detail'),
        onClick: () =>
          handleOpenEmailPreviewDialog(
            item,
            i18n.t('entity.activityPanel.activity.actions.emailSent'),
            'MESSAGE_DIALOG_EMAIL'
          ),
      }),
    }))
    .with('SMS_TO_CUSTOMER', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.smsToCustomer'),
      userName: isServiceCaseActivity
        ? (overrides?.userName ?? item.userName)
        : (item.relatedMessage?.sentTo ?? undefined),
      interestType: isServiceCaseActivity
        ? overrides?.serviceOrderNumber
        : (item.relatedMessage?.subject ?? undefined),
      description: isServiceCaseActivity
        ? (overrides?.message ?? item.relatedMessage?.sentTo ?? undefined)
        : undefined,
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      icon: 'communication/textsms',
      links: buildArray<ActivityItemDataLink>().add({
        label: i18n.t('general.labels.detail'),
        onClick: () =>
          handleOpenMessageDialog(
            item.relatedMessage?.body ?? '',
            i18n.t('entity.activityPanel.activity.actions.smsSent'),
            'MESSAGE_DIALOG_SMS'
          ),
      }),
    }))
    .with('WHATSAPP_TO_CUSTOMER', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.whatsAppToCustomer'),
      userName: overrides?.userName ?? item.userName,
      interestType: overrides?.serviceOrderNumber,
      description: overrides?.message ?? item.relatedMessage?.sentTo ?? undefined,
      timestamp: item.timestamp,
      color: IconColors.GREEN,
      icon: 'communication/whatsapp',
      links: buildArray<ActivityItemDataLink>().add({
        label: i18n.t('general.labels.detail'),
        onClick: () =>
          handleOpenMessageDialog(
            item.relatedMessage?.body ?? '',
            i18n.t('general.labels.whatsapp'),
            'MESSAGE_DIALOG_WHATSAPP'
          ),
      }),
    }))
    .with('BUSINESS_CASE_REOPENED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.businessCaseReopened'),
      userName: item.userName,
      userId: item.userId,
      color: IconColors.GREEN,
      icon: 'places/business_center',
      timestamp: item.timestamp,
      description: item.description,
    }))
    .with('CONTRACT_CANCELED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.contractCancelled'),
      userName: item.userName,
      userId: item.userId,
      color: IconColors.RED,
      icon: 'action/contract_cancelled',
      timestamp: item.timestamp,
      description: item.description,
    }))
    .with('DEPOSIT_PAYMENT_CANCELED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.depositPaymentCancelled'),
      userName: item.userName,
      userId: item.userId,
      color: IconColors.RED,
      icon: 'action/deposit_payment',
      timestamp: item.timestamp,
    }))
    .with('BALANCE_PAYMENT_CANCELED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.balancePaymentCancelled'),
      userName: item.userName,
      userId: item.userId,
      color: IconColors.RED,
      icon: 'action/invoice_issued',
      timestamp: item.timestamp,
    }))
    .with('PURCHASE_PAYMENT_CANCELED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.purchasePaymentCancelled'),
      userName: item.userName,
      userId: item.userId,
      color: IconColors.RED,
      icon: 'action/invoice_issued',
      timestamp: item.timestamp,
    }))
    .with('DEPOSIT_PAYMENT_WITHDRAWN', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.depositPaymentWithdrawn'),
      userName: item.userName,
      userId: item.userId,
      color: IconColors.RED,
      icon: 'action/deposit_payment',
      timestamp: item.timestamp,
    }))
    .with('BALANCE_PAYMENT_WITHDRAWN', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.balancePaymentWithdrawn'),
      userName: item.userName,
      userId: item.userId,
      color: IconColors.RED,
      icon: 'action/invoice_issued',
      timestamp: item.timestamp,
    }))
    .with('PURCHASE_PAYMENT_WITHDRAWN', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.purchasePaymentWithdrawn'),
      userName: item.userName,
      userId: item.userId,
      color: IconColors.RED,
      icon: 'action/invoice_issued',
      timestamp: item.timestamp,
    }))
    .with('FULL_PAYMENT_CORRECTIVE_DOCUMENT_CREATED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.correctiveDocumentCreated'),
      userName: item.userName,
      userId: item.userId,
      color: IconColors.GREEN,
      icon: 'action/invoice_issued',
      timestamp: item.timestamp,
    }))
    .with('PDF_OFFER_SENT', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.pdfOfferSent'),
      userName: item.userName,
      userId: item.userId,
      color: IconColors.GREEN,
      icon: 'content/drafts',
      timestamp: item.timestamp,
      links: buildArray<ActivityItemDataLink>().add({
        label: i18n.t('general.labels.detail'),
        onClick: () =>
          handleOpenEmailPreviewDialog(
            item,
            i18n.t('entity.activityPanel.activity.pdfOfferSent'),
            'PDF_OFFER_SENT_DIALOG'
          ),
      }),
    }))
    .with('CHAT_WITH_CUSTOMER', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.chatWithCustomer'),
      userName: overrides?.userName ?? item.userName,
      userId: item.userId,
      color: IconColors.BLUE,
      icon: 'communication/email',
      description: overrides?.message,
      links: buildArray<ActivityItemDataLink>().add({
        label: i18n.t('entity.activityPanel.activity.openChat'),
        onClick: () => handleToggleTab?.('chat'),
      }),
    }))
    .with('TEST_DRIVE_PLANNED', () => {
      const scheduledAt = getRelatedTestDriveScheduledAt(item);

      return {
        id: item.id,
        color: IconColors.BLUE,
        title: i18n.t('entity.activityPanel.activity.testDrivePlanned'),
        icon: 'automotive/test_drive_planned',
        userName: item.userName,
        userId: item.userId,
        timestamp: item.timestamp,
        relatedTestDrive: {
          vehicleTitle: overrides?.vehicleTitle ?? null,
          scheduledAt,
        },
      };
    })
    .with('TEST_DRIVE_CANCELLED', () => {
      const scheduledAt = getRelatedTestDriveScheduledAt(item);

      return {
        id: item.id,
        color: IconColors.RED,
        title: i18n.t('entity.activityPanel.activity.testDriveCanceled'),
        icon: 'automotive/test_drive_canceled',
        userName: item.userName,
        userId: item.userId,
        timestamp: item.timestamp,
        relatedTestDrive: {
          vehicleTitle: overrides?.vehicleTitle ?? null,
          scheduledAt,
        },
      };
    })
    .with('TEST_DRIVE_COMPLETED', () => {
      const scheduledAt = getRelatedTestDriveScheduledAt(item);

      return {
        id: item.id,
        color: IconColors.GREEN,
        title: i18n.t('entity.activityPanel.activity.testDriveCompleted'),
        icon: 'automotive/test_drive_completed',
        userName: item.userName,
        userId: item.userId,
        timestamp: item.timestamp,
        relatedTestDrive: {
          vehicleTitle: overrides?.vehicleTitle ?? null,
          scheduledAt,
        },
      };
    })
    .with('TEST_DRIVE_DELETED', () => {
      const scheduledAt = getRelatedTestDriveScheduledAt(item);

      return {
        id: item.id,
        color: IconColors.RED,
        title: i18n.t('entity.activityPanel.activity.testDriveDeleted'),
        icon: 'automotive/test_drive_canceled',
        userName: item.userName,
        userId: item.userId,
        timestamp: item.timestamp,
        relatedTestDrive: {
          vehicleTitle: overrides?.vehicleTitle ?? null,
          scheduledAt,
        },
      };
    })
    .with('THANK_YOU_AFTER_SALE_SENT', () =>
      getSentAfterSaleItem(item, i18n.t('entity.activityPanel.activity.thankYouAfterSaleSent'))
    )
    .with('CHECK_IN_AFTER_SALE_SENT', () =>
      getSentAfterSaleItem(item, i18n.t('entity.activityPanel.activity.checkInAfterSaleSent'))
    )
    .with('CHECK_IN_AFTER_SALE_SKIPPED_NO_CONTACT', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.checkInAfterSaleSkippedNoContact'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'content/block',
    }))
    .with('CHECK_IN_AFTER_SALE_SKIPPED_CUSTOMER_REFUSED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.checkInAfterSaleSkippedCustomerRefused'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'content/block',
    }))
    .with('CHECK_IN_AFTER_SALE_SKIPPED_ALREADY_RATED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.checkInAfterSaleSkippedAlreadyRated'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'content/block',
    }))
    .with('CHECK_IN_AFTER_SALE_SKIPPED_DISABLED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.checkInAfterSaleSkippedDisabled'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'content/block',
    }))
    .with('CHECK_IN_AFTER_SALE_SKIPPED_ONLINE_OFFER_UNAVAILABLE', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.checkInAfterSaleSkippedOnlineOfferUnavailable'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'content/block',
    }))
    .with('CHECK_IN_AFTER_SALE_CANCELLED_REOPENED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.checkInAfterSaleCancelledReopened'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'navigation/cancel',
    }))
    .with('CHECK_IN_AFTER_SALE_CANCELLED_UNSUCCESSFUL', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.checkInAfterSaleCancelledUnsuccessful'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'navigation/cancel',
    }))
    .with('CUSTOMER_REFUSED_COMMUNICATION', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.customerRefusedCommunication'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'content/block',
    }))
    .with('CUSTOMER_RATED_PURCHASE', () => ({
      id: item.id,
      title: getCustomerRatingTitle(item),
      timestamp: item.timestamp,
      color: IconColors.BLUE,
      icon: 'action/grade',
    }))
    .with('THANK_YOU_AFTER_SALE_SKIPPED_NO_CONTACT', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.thankYouAfterSaleSkippedNoContact'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'content/block',
    }))
    .with('THANK_YOU_AFTER_SALE_SKIPPED_CUSTOMER_REFUSED', () => ({
      id: item.id,
      title: i18n.t('entity.activityPanel.activity.thankYouAfterSaleSkippedCustomerRefused'),
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'content/block',
    }))
    .with(
      'AUTOMATIC_CUSTOMER_MESSAGE_SENT',
      'AUTOMATIC_CUSTOMER_MESSAGE_SENT_WITHOUT_DOCUMENT',
      (activityType) => {
        const isSms = 'relatedResourceId' in item && item.relatedResourceId === SMS_RESOURCE_ID;
        return {
          id: item.id,
          timestamp: item.timestamp,
          color: IconColors.GREEN,
          title: i18n.t(
            isSms
              ? 'entity.activityPanel.activity.automaticCustomerMessageSmsSent'
              : 'entity.activityPanel.activity.automaticCustomerMessageEmailSent',
            {messageName: getAutomaticCustomerMessageName(item)}
          ),
          icon: isSms ? 'communication/textsms' : 'communication/email',
          userName: item.relatedMessage?.sentTo ?? undefined,
          interestType: item.relatedMessage?.subject ?? undefined,
          description:
            activityType === 'AUTOMATIC_CUSTOMER_MESSAGE_SENT_WITHOUT_DOCUMENT'
              ? i18n.t('entity.activityPanel.activity.automaticCustomerMessageSentWithoutDocument')
              : undefined,
          links: buildArray<ActivityItemDataLink>().when(isNotNil(item.relatedMessage), {
            label: i18n.t('general.labels.detail'),
            onClick: () =>
              isSms
                ? handleOpenMessageDialog(
                    item.relatedMessage?.body ?? '',
                    i18n.t('entity.activityPanel.activity.actions.smsSent'),
                    'MESSAGE_DIALOG_SMS'
                  )
                : handleOpenEmailPreviewDialog(
                    item,
                    i18n.t('entity.activityPanel.activity.actions.emailSent'),
                    'MESSAGE_DIALOG_EMAIL'
                  ),
          }),
        };
      }
    )
    .with('AUTOMATIC_CUSTOMER_MESSAGE_SKIPPED_NO_CONTACT', () => ({
      id: item.id,
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'content/block',
      title: i18n.t('entity.activityPanel.activity.automaticCustomerMessageSkippedNoContact', {
        messageName: getAutomaticCustomerMessageName(item),
      }),
    }))
    .with('AUTOMATIC_CUSTOMER_MESSAGE_SKIPPED_CUSTOMER_REFUSED', () => ({
      id: item.id,
      timestamp: item.timestamp,
      color: IconColors.RED,
      icon: 'content/block',
      title: i18n.t(
        'entity.activityPanel.activity.automaticCustomerMessageSkippedCustomerRefused',
        {
          messageName: getAutomaticCustomerMessageName(item),
        }
      ),
    }))
    .otherwise(() => null);
};
